/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  pageFromPathname,
  replaceBrowserPath,
  searchForProcureOrders,
  procureOrdersQueryFromSearch,
} from "../../routing/paths";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { readSelectedStore, writeSelectedStore } from "../../lib/selectedStore";
import { allowedStoresForUser, isSingleStoreLocked } from "../../lib/storeScope";
import { LoadingPulse } from "../../ui";
import {
  procureiqApiError,
  procureiqLocationKey,
  procureiqResolveLocationKey,
  procureiqLocationWithBrandLabel,
  procureiqLocationDotColor,
} from "./helpers";
import { ReceiptReviewPanel } from "./ReceiptReviewPanel";

/**
 * Standalone Ops section: verify / dismiss Supy order receipts.
 * Separate from ProcureIQ ordering tabs.
 */
export function ProcureOrdersPage({ user, lang }) {
  const { storeMap } = usePgStores();
  const isStoreLocked = isSingleStoreLocked(user);
  const allowed = allowedStoresForUser(user);
  const canPickStore =
    ["admin", "brand_manager", "hr"].includes(user.role) ||
    (Array.isArray(allowed) && allowed.length > 1);
  const lockedStoreName = isStoreLocked ? (allowed || [])[0] || null : null;

  const initialQuery = useMemo(
    () =>
      procureOrdersQueryFromSearch(
        typeof window !== "undefined" ? window.location.search : ""
      ),
    []
  );

  const [activeLocations, setActiveLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(canPickStore);
  const [locationsError, setLocationsError] = useState(null);
  const [selectedLocationKey, setSelectedLocationKey] = useState(null);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [toast, setToast] = useState(null);

  const t = (en, gr) => (lang === "gr" ? gr : en);

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  useEffect(() => {
    if (!canPickStore) {
      setActiveLocations([]);
      setLocationsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLocationsLoading(true);
      setLocationsError(null);
      try {
        const res = await fetch(`${SHIFTIQ_API}/api/procureiq/active-locations`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(await procureiqApiError(res, "Locations failed"));
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data?.locations) ? data.locations : [];
        setActiveLocations(list);
        const remembered = readSelectedStore();
        const fromUrl = initialQuery.locationId;
        const key =
          (fromUrl != null
            ? procureiqResolveLocationKey(list, fromUrl, initialQuery.brandId)
            : null) ||
          (remembered?.locationId != null
            ? procureiqResolveLocationKey(list, remembered.locationId, remembered.brandId)
            : null) ||
          (list[0] ? procureiqLocationKey(list[0]) : null);
        setSelectedLocationKey(key);
      } catch (err) {
        if (cancelled || isAbortError(err)) return;
        setLocationsError(err?.message || "Error");
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canPickStore]);

  const selectedLocation = useMemo(() => {
    if (!canPickStore) return null;
    return (
      activeLocations.find((loc) => procureiqLocationKey(loc) === selectedLocationKey) ||
      activeLocations[0] ||
      null
    );
  }, [canPickStore, activeLocations, selectedLocationKey]);

  const locationId = canPickStore
    ? selectedLocation?.unified_location_id ?? null
    : lockedStoreName
      ? storeMap[lockedStoreName] ?? null
      : null;
  const brandId = canPickStore ? selectedLocation?.brand_id || 15 : 15;

  const prevStoreRef = useRef({ locationId: null, brandId: null });
  useEffect(() => {
    if (pageFromPathname(window.location.pathname) !== "procure-orders") return;
    const prev = prevStoreRef.current;
    const storeChanged =
      prev.locationId != null &&
      (prev.locationId !== locationId || prev.brandId !== brandId);
    prevStoreRef.current = { locationId, brandId };
    const current = procureOrdersQueryFromSearch(window.location.search);
    replaceBrowserPath(
      "/procure-orders",
      searchForProcureOrders({
        locationId,
        brandId: canPickStore ? brandId : undefined,
        // Keep deep-linked order unless the store picker changed.
        orderKey: storeChanged ? null : current.orderKey,
      })
    );
    if (locationId != null) writeSelectedStore(locationId, brandId);
  }, [locationId, brandId, canPickStore]);

  useEffect(() => {
    function onPop() {
      if (pageFromPathname(window.location.pathname) !== "procure-orders") return;
      const query = procureOrdersQueryFromSearch(window.location.search);
      if (query.locationId == null) return;
      setSelectedLocationKey((prev) => {
        return (
          procureiqResolveLocationKey(activeLocations, query.locationId, query.brandId) ||
          prev
        );
      });
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [activeLocations]);

  const selectedLabel = canPickStore
    ? procureiqLocationWithBrandLabel(selectedLocation) || t("Select store", "Επιλογή καταστήματος")
    : lockedStoreName || t("Store", "Κατάστημα");
  const selectedDot = canPickStore
    ? procureiqLocationDotColor(selectedLocation)
    : "var(--green)";

  return (
    <div className="page-pad">
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontFamily: "var(--font-display, Bebas Neue, sans-serif)", fontSize: 28, margin: 0 }}>
          {t("Procure Orders", "Παραγγελίες προμηθειών")}
        </h1>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 6 }}>
          {t(
            "Upload invoice photos first, then match and verify so food-cost uses the right delivery date.",
            "Ανεβάστε πρώτα φωτο τιμολογίων, μετά αντιστοιχίστε και επιβεβαιώστε ώστε το food-cost να χρησιμοποιεί τη σωστή ημερομηνία."
          )}
        </p>
      </div>

      {canPickStore ? (
        <button
          type="button"
          className="info-pill"
          style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8, width: "100%" }}
          onClick={() => setShowStorePicker(true)}
        >
          <span
            style={{ width: 10, height: 10, borderRadius: "50%", background: selectedDot, flexShrink: 0 }}
          />
          <span style={{ flex: 1, textAlign: "left", fontWeight: 700 }}>
            {locationsLoading ? t("Loading…", "Φόρτωση…") : selectedLabel}
          </span>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>▾</span>
        </button>
      ) : (
        <div className="info-pill" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: selectedDot, flexShrink: 0 }} />
          {selectedLabel}
        </div>
      )}

      {locationsError && (
        <div className="card-box" style={{ marginBottom: 14, borderColor: "#FACFC8", background: "#FEF0ED" }}>
          <p style={{ color: "var(--danger)", fontWeight: 700 }}>{locationsError}</p>
        </div>
      )}

      {!locationId && locationsLoading && <LoadingPulse label={t("Loading stores…", "Φόρτωση καταστημάτων…")} />}
      {!locationId && !locationsLoading && (
        <div className="card-box">
          <p style={{ color: "var(--text-muted)" }}>
            {t("Select a store to review orders.", "Επιλέξτε κατάστημα για έλεγχο παραγγελιών.")}
          </p>
        </div>
      )}

      {locationId && (
        <ReceiptReviewPanel
          locationId={locationId}
          locationName={
            canPickStore
              ? selectedLocation?.location_name || ""
              : lockedStoreName || ""
          }
          brandId={brandId}
          initialOrderKey={initialQuery.orderKey || null}
          lang={lang}
          t={t}
          showToast={showToast}
        />
      )}

      {showStorePicker && (
        <div className="modal-overlay" onClick={() => setShowStorePicker(false)}>
          <div
            className="card-box"
            style={{ maxWidth: 420, width: "92%", margin: "40px auto", maxHeight: "80vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, marginBottom: 12 }}>{t("Store", "Κατάστημα")}</div>
            {(activeLocations || []).map((loc) => {
              const key = procureiqLocationKey(loc);
              const active = key === selectedLocationKey;
              return (
                <button
                  key={key}
                  type="button"
                  className={`filter-chip ${active ? "filter-chip-active" : ""}`}
                  style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 8 }}
                  onClick={() => {
                    setSelectedLocationKey(key);
                    writeSelectedStore(loc.unified_location_id, loc.brand_id);
                    setShowStorePicker(false);
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: procureiqLocationDotColor(loc),
                      marginRight: 8,
                    }}
                  />
                  {procureiqLocationWithBrandLabel(loc)}
                </button>
              );
            })}
            <button
              type="button"
              className="btn-base btn-outline btn-small"
              style={{ marginTop: 8 }}
              onClick={() => setShowStorePicker(false)}
            >
              {t("Close", "Κλείσιμο")}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#111",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 80,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
